import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../ui/Input';
import LocationMapPicker from '../maps/LocationMapPicker';
import OperatingHoursEditor, { defaultOperatingHours } from './OperatingHoursEditor';

const BranchFormPanel = ({ open, onClose, branch, onSave, saving }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [hours, setHours] = useState(defaultOperatingHours());
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [location, setLocation] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (branch) {
      setName(branch.name);
      setAddress(branch.address || '');
      setPhone(branch.phone || '');
      setEmail(branch.email || '');
      setDescription(branch.description || '');
      setHours(branch.operatingHours?.length ? branch.operatingHours : defaultOperatingHours());
      setCoverPreview(branch.coverImage || null);
      setLocation(
        branch.latitude && branch.longitude
          ? { latitude: branch.latitude, longitude: branch.longitude }
          : null
      );
    } else {
      setName('');
      setAddress('');
      setPhone('');
      setEmail('');
      setDescription('');
      setHours(defaultOperatingHours());
      setCoverPreview(null);
      setLocation(null);
    }
    setCoverFile(null);
  }, [open, branch]);

  const handleFile = (file) => {
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name,
      address,
      phone,
      email,
      description,
      operatingHours: hours,
      coverFile,
      latitude: location?.latitude,
      longitude: location?.longitude,
    });
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <aside className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 p-5">
          <h2 className="text-lg font-semibold">
            {branch ? t('salon.branches.editBranch') : t('salon.branches.addBranch')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('common.close')} className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto p-5">
            <Input label={t('salon.branches.branchNameRequired')} id="name" value={name} onChange={(e) => setName(e.target.value)} required />

            <LocationMapPicker
              key={branch?._id || 'new-branch'}
              value={location}
              onChange={setLocation}
              onPlaceSelect={(label) => setAddress(label)}
              searchDefault={branch?.address || ''}
              active={open}
              height="300px"
            />

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFile(e.dataTransfer.files[0]);
              }}
              className={`rounded-xl border-2 border-dashed p-6 text-center ${
                dragOver ? 'border-glow-500 bg-glow-500/10' : 'border-zinc-300 dark:border-zinc-700'
              }`}
            >
              {coverPreview ? (
                <img src={coverPreview} alt="" className="mx-auto h-32 w-full rounded-lg object-cover" />
              ) : (
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('salon.branches.coverImageHint')}</p>
              )}
              <label className="mt-3 inline-block cursor-pointer text-sm text-glow-400 hover:underline">
                {t('salon.branches.chooseFile')}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </label>
            </div>

            <Input label={t('common.address')} id="address" value={address} onChange={(e) => setAddress(e.target.value)} />
            <Input label={t('common.phone')} id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Input label={t('common.email')} id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <div>
              <label htmlFor="description" className="mb-1 block text-sm text-zinc-600 dark:text-zinc-400">
                {t('common.description')}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm outline-none focus:border-glow-500"
              />
            </div>

            <OperatingHoursEditor hours={hours} onChange={setHours} />
          </div>

          <div className="border-t border-zinc-200 dark:border-zinc-800 p-5">
            <button
              type="submit"
              disabled={saving || !location?.latitude || !location?.longitude}
              className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50"
            >
              {saving ? t('common.saving') : t('salon.branches.saveBranch')}
            </button>
          </div>
        </form>
      </aside>
    </>
  );
};

export default BranchFormPanel;
